import { useEffect, useState } from 'react';
import { get } from '../../api.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';

/** A meeting as the sheets list it: who, when, who of Waste Robotics, HubSpot state. */
export interface MeetingRow {
  id: string;
  met_at: string;
  event_code: string | null;
  contacts: { id: string; name: string; company: string | null }[];
  attendees: string[];
  lead_id: string | null;
  notes: string | null;
  next_step: string | null;
  next_step_due: string | null;
  /** Nov 4, 2026: its title in HubSpot (none for the first meetings). */
  title?: string | null;
  created_by?: string;
  hubspot_status: string;
  hubspot_error: string | null;
  /** Saved on this device, not on the portal yet. */
  waiting?: boolean;
}

export interface Colleague {
  id: string;
  name: string;
}

const PEOPLE = 'meetingPeople';
const str = (v: unknown) => (typeof v === 'string' ? v : '');

/**
 * Who of Waste Robotics can be named (the active users), kept on the device
 * for the next time without network; the person alone until the portal answered.
 */
export function useColleagues(): Colleague[] {
  const { store } = useOffline();
  const { user } = useSignedIn();
  const me = { id: user.id, name: `${user.firstName} ${user.lastName}` };
  const [list, setList] = useState<Colleague[] | null>(null);
  useEffect(() => {
    let current = true;
    void (async () => {
      const kept = await store?.getMeta<Colleague[]>(PEOPLE).catch(() => null);
      if (kept && current) setList(kept);
      try {
        const { items } = await get<{ items: Colleague[] }>('/meetings/people');
        if (!current || !Array.isArray(items)) return;
        setList(items);
        await store?.setMeta(PEOPLE, items);
      } catch {
        // No network: the list kept, or the person alone.
      }
    })();
    return () => {
      current = false;
    };
  }, [store]);
  const all = list ?? [me];
  return all.some((c) => c.id === me.id) ? all : [me, ...all];
}

/** The meetings of a contact or a lead: the portal's, and those still on this device. */
export function useMeetings(of: { contactId?: string; leadId?: string }, reload: unknown) {
  const { store } = useOffline();
  const [rows, setRows] = useState<MeetingRow[] | null>(null);
  const key = of.contactId ? `contactId=${of.contactId}` : `leadId=${of.leadId}`;
  useEffect(() => {
    let current = true;
    void (async () => {
      const server = await get<{ items: MeetingRow[] }>(`/meetings?${key}`)
        .then((r) => r.items ?? [])
        .catch(() => [] as MeetingRow[]);
      if (!current) return;
      // The device's store may close meanwhile (sign out, locked): the portal's list stays.
      const local = store ? await deviceMeetings(store, of).catch(() => [] as MeetingRow[]) : [];
      const known = new Set(server.map((m) => m.id));
      // A change still on this device (Edit, Nov 4, 2026) shows over the portal's copy.
      const changed = new Map(local.filter((m) => m.waiting).map((m) => [m.id, m]));
      const all = [
        ...server.map((m) => {
          const c = changed.get(m.id);
          return c ? { ...m, title: c.title, notes: c.notes, waiting: true } : m;
        }),
        ...local.filter((m) => !known.has(m.id)),
      ].sort((a, b) => b.met_at.localeCompare(a.met_at));
      if (current) setRows(all);
    })();
    return () => {
      current = false;
    };
    // of: its key stands for it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, store, reload]);
  return rows;
}

/** The device's meetings of a contact or a lead, with the names it knows. */
async function deviceMeetings(
  store: LocalStore,
  of: { contactId?: string; leadId?: string },
): Promise<MeetingRow[]> {
  const [meetings, contacts, events, people] = await Promise.all([
    store.listRecords('meeting'),
    store.listRecords('contact'),
    store.listRecords('event'),
    store.getMeta<Colleague[]>(PEOPLE).catch(() => null),
  ]);
  const waiting = new Set(
    (await store.outbox())
      .filter((i) => i.op.entity === 'meeting')
      .map((i) => (i.op as { id: string }).id),
  );
  const byId = new Map(contacts.map((c) => [c.id, c]));
  return meetings
    .filter((m) =>
      of.contactId
        ? Array.isArray(m.contact_ids) && (m.contact_ids as string[]).includes(of.contactId)
        : m.lead_id === of.leadId,
    )
    .map((m) => toRow(m, byId, events, people ?? [], waiting.has(m.id)));
}

function toRow(
  m: LocalRecord,
  contacts: Map<string, LocalRecord>,
  events: LocalRecord[],
  people: Colleague[],
  waiting: boolean,
): MeetingRow {
  const ids = Array.isArray(m.contact_ids) ? (m.contact_ids as string[]) : [];
  const attendees = Array.isArray(m.attendee_ids) ? (m.attendee_ids as string[]) : [];
  return {
    id: m.id,
    met_at: str(m.met_at),
    event_code: str(events.find((e) => e.id === m.event_id)?.event_code) || null,
    contacts: ids.map((id) => {
      const c = contacts.get(id);
      return {
        id,
        name: [str(c?.first_name), str(c?.last_name)].filter(Boolean).join(' ') || 'Contact',
        company: str(c?.company) || null,
      };
    }),
    attendees: attendees.map((id) => people.find((p) => p.id === id)?.name ?? 'A colleague'),
    lead_id: str(m.lead_id) || null,
    notes: str(m.notes) || null,
    title: str(m.title) || null,
    created_by: str(m.created_by),
    next_step: str(m.next_step) || null,
    next_step_due: str(m.next_step_due) || null,
    hubspot_status: str(m.hubspot_status) || 'pending',
    hubspot_error: str(m.hubspot_error) || null,
    waiting,
  };
}

/** The local date and time of now, for a datetime-local field. */
export function localNow(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Today's local date, YYYY-MM-DD. */
export const localToday = () => localNow().slice(0, 10);
